import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { TableWrapper, TableHead, FilterRow } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { WithWrite } from '../lib/rbac';
import { settingsApi } from '../services/settings';
import {
  productsApi,
  type Product,
  type ProductComponent,
  type ProductDetail,
  type ProductFormData,
} from '../services/products';

type ProductComponentFormRow = {
  componentProductId: number | '';
  qty: string;
  componentSearch: string;
};

const EMPTY_FORM: ProductFormData = {
  name: '',
  kind: 'BULK',
  parentProductId: null,
  categoryItemId: null,
  defaultUomItemId: null,
  defaultVatRateItemId: null,
  netWeightG: null,
  canPurchase: true,
  canSell: true,
  salesPriceRetailIncVat: null,
  salesPriceDefaultExVat: null,
  syncWooCommerce: false,
  syncZettle: false,
  syncGs1: false,
  syncFortnox: false,
  metaJson: null,
  notes: '',
  sku: '',
  ean: '',
  components: [],
};

const EMPTY_COMPONENT_ROW: ProductComponentFormRow = {
  componentProductId: '',
  qty: '1',
  componentSearch: '',
};

function formatComponentRows(components: ProductComponent[] | undefined): ProductComponentFormRow[] {
  if (!components || components.length === 0) {
    return [];
  }

  return components.map((component) => ({
    componentProductId: component.componentProductId,
    qty:
      component.componentUomLabel === 'st'
        ? String(Math.round(component.qty))
        : component.qty
          ? String(component.qty)
          : '',
    componentSearch: `${component.componentSku ?? '-'} - ${component.componentName ?? ''}`.trim(),
  }));
}

function preparationMetaFromForm(form: ProductFormData) {
  return form.metaJson?.preparation ?? {
    legacyPrepId: null,
    withdrawalDays: null,
    treatmentLengthDays: null,
    instructionUrl: '',
  };
}

export function ProductsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [valueListItems, setValueListItems] = useState<Record<string, Array<{ id: number; label: string }>>>({});
  const [filter, setFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [purchaseFilter, setPurchaseFilter] = useState('');
  const [salesFilter, setSalesFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ProductDetail | null>(null);
  const [form, setForm] = useState<ProductFormData>(EMPTY_FORM);
  const [componentRows, setComponentRows] = useState<ProductComponentFormRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);
  const [openComponentSearchIndex, setOpenComponentSearchIndex] = useState<number | null>(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const toast = useToast();
  const { canRead } = useAuth();
  const editProductId = Number(searchParams.get('edit') ?? '');

  const formatPrice = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) {
      return String(value);
    }
    return `${numberValue.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;
  };

  const integrationSummary = (product: Product) => {
    const channels = [];
    if (product.syncWooCommerce) channels.push('Woo');
    if (product.syncZettle) channels.push('Zettle');
    if (product.syncGs1) channels.push('GS1');
    if (product.syncFortnox) channels.push('Fortnox');
    return channels.length > 0 ? channels.join(', ') : '-';
  };

  if (!canRead('products')) {
    return <Navigate to="/" replace />;
  }

  const loadProducts = useCallback(async () => {
    try {
      const res = await productsApi.list();
      setProducts(res.products);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte hämta produkter');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadValueLists = useCallback(async () => {
    try {
      const [cat, uom, vat] = await Promise.all([
        settingsApi.getValueListItems('PRODUCT_CATEGORY'),
        settingsApi.getValueListItems('UOM'),
        settingsApi.getValueListItems('VAT_RATES'),
      ]);
      setValueListItems({
        PRODUCT_CATEGORY: cat.items.filter((item) => !item.isHidden).map((item) => ({ id: item.id, label: item.label })),
        UOM: uom.items.filter((item) => !item.isHidden).map((item) => ({ id: item.id, label: item.label })),
        VAT_RATES: vat.items.filter((item) => !item.isHidden).map((item) => ({ id: item.id, label: item.label })),
      });
    } catch {
      // Dropdowns ska inte blockera sidan om de inte laddar.
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadValueLists();
  }, [loadProducts, loadValueLists]);

  const productOptions = useMemo(() => {
    const uniqueProducts = new Map<number, Product>();
    for (const product of products) {
      if (!uniqueProducts.has(product.id)) {
        uniqueProducts.set(product.id, product);
      }
    }
    return Array.from(uniqueProducts.values()).map((product) => ({
      ...product,
      optionLabel: `${product.sku ?? '-'} - ${product.name}`,
    }));
  }, [products]);
  const selectedCategoryLabel = useMemo(
    () => (valueListItems.PRODUCT_CATEGORY ?? []).find((item) => item.id === form.categoryItemId)?.label ?? null,
    [form.categoryItemId, valueListItems]
  );
  const isPreparationProduct = selectedCategoryLabel === 'Preparat';

  const resetForm = () => {
    setEditing(null);
    setShowForm(false);
    setForm(EMPTY_FORM);
    setComponentRows([]);
    setConfirmDeleteIndex(null);
    setOpenComponentSearchIndex(null);
    setShowDeactivateConfirm(false);
    if (searchParams.has('edit')) {
      setSearchParams({}, { replace: true });
    }
  };

  const filtered = products.filter((product) => {
    const textMatch =
      !filter ||
      product.name.toLowerCase().includes(filter.toLowerCase()) ||
      (product.sku && product.sku.toLowerCase().includes(filter.toLowerCase()));

    const kindMatch = !kindFilter || product.kind === kindFilter;
    const categoryMatch = !categoryFilter || String(product.categoryItemId ?? '') === categoryFilter;
    const purchaseMatch = !purchaseFilter || (purchaseFilter === 'yes' ? !!product.canPurchase : !product.canPurchase);
    const salesMatch = !salesFilter || (salesFilter === 'yes' ? !!product.canSell : !product.canSell);

    return textMatch && kindMatch && categoryMatch && purchaseMatch && salesMatch;
  });

  const columnCount = 13;

  const buildComponentPayload = (): Array<{ componentProductId: number; qty: number }> => {
    return componentRows
      .filter((row) => row.componentProductId !== '' && row.qty.trim() !== '')
      .map((row) => ({
        componentProductId: Number(row.componentProductId),
        qty: Number(row.qty),
      }))
      .filter((row) => row.componentProductId > 0 && row.qty > 0);
  };

  const openCreate = () => {
    setSearchParams({}, { replace: true });
    setEditing(null);
    setForm(EMPTY_FORM);
    setComponentRows([]);
    setShowDeactivateConfirm(false);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Namn krävs');
      return;
    }

    const componentPayload = buildComponentPayload();
    const payload: ProductFormData = {
      ...form,
      components: componentPayload,
    };

    setSaving(true);
    try {
      if (editing) {
        await productsApi.update(editing.id, payload);
        toast.success('Produkt uppdaterad');
      } else {
        await productsApi.create(payload);
        toast.success('Produkt skapad');
      }
      resetForm();
      await loadProducts();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte spara produkt');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (product: Product) => {
    setLoadingDetail(true);
    try {
      const detail = await productsApi.get(product.id);
      setEditing(detail);
      setForm({
        sku: detail.sku ?? '',
        ean: detail.ean ?? '',
        parentProductId: detail.parentProductId,
        name: detail.name,
        kind: detail.kind,
        categoryItemId: detail.categoryItemId,
        defaultUomItemId: detail.defaultUomItemId,
        netWeightG: detail.netWeightG,
        canPurchase: !!detail.canPurchase,
        canSell: !!detail.canSell,
        salesPriceRetailIncVat: detail.salesPriceRetailIncVat !== null ? Number(detail.salesPriceRetailIncVat) : null,
        salesPriceDefaultExVat: detail.salesPriceDefaultExVat !== null ? Number(detail.salesPriceDefaultExVat) : null,
        syncWooCommerce: !!detail.syncWooCommerce,
        syncZettle: !!detail.syncZettle,
        syncGs1: !!detail.syncGs1,
        syncFortnox: !!detail.syncFortnox,
        defaultVatRateItemId: detail.defaultVatRateItemId,
        metaJson: detail.metaJson ?? null,
        notes: detail.notes ?? '',
        components: detail.components.map((component) => ({
          componentProductId: component.componentProductId,
          qty: component.qty,
        })),
      });
      setComponentRows(formatComponentRows(detail.components));
      setShowDeactivateConfirm(false);
      setShowForm(true);
      setSearchParams({ edit: String(product.id) }, { replace: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ladda produkt');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDelete = async (product: Product) => {
    setDeletingProduct(true);
    try {
      await productsApi.delete(product.id);
      toast.success('Produkt inaktiverad');
      if (editing?.id === product.id) {
        resetForm();
      }
      await loadProducts();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte inaktivera produkt');
    } finally {
      setDeletingProduct(false);
      setShowDeactivateConfirm(false);
    }
  };

  useEffect(() => {
    if (loading || loadingDetail || showForm || !Number.isFinite(editProductId) || editProductId <= 0) {
      return;
    }

    const product = products.find((item) => item.id === editProductId);
    if (!product) {
      setSearchParams({}, { replace: true });
      return;
    }

    void handleEdit(product);
  }, [editProductId, handleEdit, loading, loadingDetail, products, setSearchParams, showForm]);

  const updateComponentRow = (index: number, patch: Partial<ProductComponentFormRow>) => {
    setComponentRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const removeComponentRow = (index: number) => {
    setComponentRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
    setConfirmDeleteIndex(null);
    setOpenComponentSearchIndex((current) => {
      if (current === null) {
        return null;
      }
      if (current === index) {
        return null;
      }
      return current > index ? current - 1 : current;
    });
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Produkter</h1>
        <WithWrite moduleCode="products">
          <button
            type="button"
            onClick={openCreate}
            className="px-2 py-1 text-sm bg-success text-white rounded hover:bg-success-hover"
          >
            Ny produkt
          </button>
        </WithWrite>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          placeholder="Filtrera på namn eller artikelnummer"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-64"
        />
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="w-36">
          <option value="">Alla typer</option>
          <option value="BULK">BULK</option>
          <option value="INSTANCE">INSTANCE</option>
          <option value="SERVICE">SERVICE</option>
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="w-44">
          <option value="">Alla kategorier</option>
          {(valueListItems.PRODUCT_CATEGORY ?? []).map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>
        <select value={purchaseFilter} onChange={(e) => setPurchaseFilter(e.target.value)} className="w-36">
          <option value="">Alla inköp</option>
          <option value="yes">Kan köpas in</option>
          <option value="no">Kan ej köpas in</option>
        </select>
        <select value={salesFilter} onChange={(e) => setSalesFilter(e.target.value)} className="w-36">
          <option value="">All försäljning</option>
          <option value="yes">Kan säljas</option>
          <option value="no">Kan ej säljas</option>
        </select>
      </div>

      <TableWrapper>
        <TableHead>
          <tr>
            <th className="text-left px-2 py-1.5 font-medium">Artikelnummer</th>
            <th className="text-left px-2 py-1.5 font-medium">Namn</th>
            <th className="text-left px-2 py-1.5 font-medium">Huvudartikel</th>
            <th className="text-left px-2 py-1.5 font-medium">Typ</th>
            <th className="text-left px-2 py-1.5 font-medium">Kategori</th>
            <th className="text-left px-2 py-1.5 font-medium">Enhet</th>
            <th className="text-left px-2 py-1.5 font-medium">Köp</th>
            <th className="text-left px-2 py-1.5 font-medium">Sälj</th>
            <th className="text-left px-2 py-1.5 font-medium">Privat inkl.</th>
            <th className="text-left px-2 py-1.5 font-medium">Företag exkl.</th>
            <th className="text-left px-2 py-1.5 font-medium">Nettovikt</th>
            <th className="text-left px-2 py-1.5 font-medium">Moms</th>
            <th className="text-left px-2 py-1.5 font-medium">Kanaler</th>
          </tr>
          <FilterRow>
            <td colSpan={columnCount} className="px-2 py-1" />
          </FilterRow>
        </TableHead>
        <tbody>
          {filtered.map((product) => (
            <tr
              key={product.id}
              className="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
              onClick={() => navigate(`/products/${product.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/products/${product.id}`);
                }
              }}
              tabIndex={0}
              role="link"
            >
              <td className="px-2 py-1">{product.sku ?? '-'}</td>
              <td className="px-2 py-1 font-medium">{product.name}</td>
              <td className="px-2 py-1">
                {product.parentName ? `${product.parentSku ? `${product.parentSku} - ` : ''}${product.parentName}` : '-'}
              </td>
              <td className="px-2 py-1">{product.kind}</td>
              <td className="px-2 py-1">{product.categoryLabel ?? '-'}</td>
              <td className="px-2 py-1">{product.uomLabel ?? '-'}</td>
              <td className="px-2 py-1">{product.canPurchase ? 'Ja' : 'Nej'}</td>
              <td className="px-2 py-1">{product.canSell ? 'Ja' : 'Nej'}</td>
              <td className="px-2 py-1">{formatPrice(product.salesPriceRetailIncVat)}</td>
              <td className="px-2 py-1">{formatPrice(product.salesPriceDefaultExVat)}</td>
              <td className="px-2 py-1">{product.netWeightG ? `${product.netWeightG} g` : '-'}</td>
              <td className="px-2 py-1">{product.vatLabel ?? '-'}</td>
              <td className="px-2 py-1">{integrationSummary(product)}</td>
            </tr>
          ))}
        </tbody>
      </TableWrapper>

      {filtered.length === 0 && <p className="text-sm text-gray-500">Inga produkter eller inga träffar.</p>}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded shadow-lg max-w-4xl w-full p-4 space-y-3 max-h-[90vh] overflow-y-auto">
            <h3 className="font-semibold">{editing ? 'Redigera produkt' : 'Ny produkt'}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-600">Namn *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600">Artikelnummer</label>
                <input
                  value={form.sku ?? ''}
                  onChange={(e) => setForm((current) => ({ ...current, sku: e.target.value }))}
                  placeholder={editing ? '' : 'Lämna tomt för automatisk generering'}
                  className="w-full"
                />
                {!editing && <p className="mt-1 text-xs text-gray-500">Lämna tomt så skapas artikelnummer automatiskt vid sparning.</p>}
              </div>

              <div>
                <label className="block text-sm text-gray-600">EAN / GTIN</label>
                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  {form.syncGs1 ? (form.ean?.trim() ? form.ean : 'Skapas automatiskt vid sparning') : 'Ingen kod så länge GS1 inte är valt'}
                </div>
                <p className="mt-1 text-xs text-gray-500">Gamla EAN-koder används inte längre. När GS1 är valt tar systemet nästa lediga GTIN-13 automatiskt.</p>
              </div>

              <div>
                <label className="block text-sm text-gray-600">Utpris till privatkund inkl. moms</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.salesPriceRetailIncVat ?? ''}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      salesPriceRetailIncVat: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600">Utpris till företagskund exkl. moms</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.salesPriceDefaultExVat ?? ''}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      salesPriceDefaultExVat: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600">Typ</label>
                <select
                  value={form.kind}
                  onChange={(e) => setForm((current) => ({ ...current, kind: e.target.value }))}
                  className="w-full"
                >
                  <option value="BULK">BULK</option>
                  <option value="INSTANCE">INSTANCE</option>
                  <option value="SERVICE">SERVICE</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600">Huvudartikel</label>
                <select
                  value={form.parentProductId ?? ''}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      parentProductId: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                  className="w-full"
                >
                  <option value="">-</option>
                  {productOptions
                    .filter((product) => product.id !== editing?.id)
                    .map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.optionLabel}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600">Kategori</label>
                <select
                  value={form.categoryItemId ?? ''}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      categoryItemId: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                  className="w-full"
                >
                  <option value="">-</option>
                  {(valueListItems.PRODUCT_CATEGORY ?? []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600">Enhet</label>
                <select
                  value={form.defaultUomItemId ?? ''}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      defaultUomItemId: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                  className="w-full"
                >
                  <option value="">-</option>
                  {(valueListItems.UOM ?? []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600">Moms</label>
                <select
                  value={form.defaultVatRateItemId ?? ''}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      defaultVatRateItemId: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                  className="w-full"
                >
                  <option value="">-</option>
                  {(valueListItems.VAT_RATES ?? []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600">Nettovikt (gram)</label>
                <input
                  type="number"
                  min={0}
                  value={form.netWeightG ?? ''}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      netWeightG: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!form.canPurchase}
                    onChange={(e) => setForm((current) => ({ ...current, canPurchase: e.target.checked }))}
                  />
                  Kan köpas in
                </label>

                <label className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!form.canSell}
                    onChange={(e) => setForm((current) => ({ ...current, canSell: e.target.checked }))}
                  />
                  Kan säljas
                </label>
              </div>

              <div className="md:col-span-2 space-y-2 rounded border border-gray-200 bg-gray-50 p-3">
                <div>
                  <h4 className="font-medium text-gray-900">Integrationer</h4>
                  <p className="text-sm text-gray-600">ApisAI är källan. Markera bara de kanaler där produkten verkligen ska finnas.</p>
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <label className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!form.syncWooCommerce}
                      onChange={(e) => setForm((current) => ({ ...current, syncWooCommerce: e.target.checked }))}
                    />
                    WooCommerce
                  </label>
                  <label className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!form.syncZettle}
                      onChange={(e) => setForm((current) => ({ ...current, syncZettle: e.target.checked }))}
                    />
                    Zettle
                  </label>
                  <label className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!form.syncGs1}
                      onChange={(e) => setForm((current) => ({ ...current, syncGs1: e.target.checked }))}
                    />
                    GS1 / EAN
                  </label>
                  <label className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!form.syncFortnox}
                      onChange={(e) => setForm((current) => ({ ...current, syncFortnox: e.target.checked }))}
                    />
                    Fortnox
                  </label>
                </div>
                <div className="text-xs text-gray-500">
                  WooCommerce och Zettle kräver privatpris inkl. moms. Fortnox kräver företagspris exkl. moms. GS1 allokerar nästa lediga GTIN-13 automatiskt.
                </div>
              </div>
            </div>

            {isPreparationProduct && (
              <div className="space-y-3 rounded border border-orange-200 bg-orange-50 p-3">
                <div>
                  <h4 className="font-medium text-gray-900">Preparatdata</h4>
                  <p className="text-sm text-gray-600">Används i kuploggen för karens, behandlingstid och säkerhetsblad.</p>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="block text-sm text-gray-600">Karenstid (dagar)</label>
                    <input
                      type="number"
                      min={0}
                      value={preparationMetaFromForm(form).withdrawalDays ?? ''}
                      onChange={(e) =>
                        setForm((current) => ({
                          ...current,
                          metaJson: {
                            ...(current.metaJson ?? {}),
                            preparation: {
                              ...preparationMetaFromForm(current),
                              withdrawalDays: e.target.value === '' ? null : Number(e.target.value),
                            },
                          },
                        }))
                      }
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600">Behandlingslängd (dagar)</label>
                    <input
                      type="number"
                      min={0}
                      value={preparationMetaFromForm(form).treatmentLengthDays ?? ''}
                      onChange={(e) =>
                        setForm((current) => ({
                          ...current,
                          metaJson: {
                            ...(current.metaJson ?? {}),
                            preparation: {
                              ...preparationMetaFromForm(current),
                              treatmentLengthDays: e.target.value === '' ? null : Number(e.target.value),
                            },
                          },
                        }))
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-600">Instruktion / säkerhetsblad URL</label>
                    <input
                      value={preparationMetaFromForm(form).instructionUrl ?? ''}
                      onChange={(e) =>
                        setForm((current) => ({
                          ...current,
                          metaJson: {
                            ...(current.metaJson ?? {}),
                            preparation: {
                              ...preparationMetaFromForm(current),
                              instructionUrl: e.target.value,
                            },
                          },
                        }))
                      }
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h4 className="font-medium">Ingående artiklar</h4>
                  <p className="text-sm text-gray-500">Lägg till emballage eller andra artiklar som ingår i produkten.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setComponentRows((current) => [...current, EMPTY_COMPONENT_ROW])}
                  className="px-2 py-1 text-sm bg-success text-white rounded hover:bg-success-hover"
                >
                  Lägg till rad
                </button>
              </div>

              <div className="space-y-2">
                {componentRows.length === 0 && (
                  <p className="text-sm text-gray-500">Inga ingående artiklar registrerade.</p>
                )}
                {componentRows.map((row, index) => {
                  const selectedProduct =
                    row.componentProductId === ''
                      ? null
                      : productOptions.find((product) => product.id === Number(row.componentProductId)) ?? null;
                  const isPieceUom = selectedProduct?.uomLabel === 'st';

                  return (
                    <div
                      key={`${index}-${selectedProduct?.id ?? 'empty'}`}
                      className="grid grid-cols-10 gap-2 items-end rounded border border-gray-200 p-3"
                    >
                      <div className="relative col-span-5">
                        <label className="block text-sm text-gray-600">Artikel</label>
                        <input
                          type="text"
                          value={row.componentSearch}
                          placeholder="Sök artikelnummer eller namn"
                          onFocus={() => setOpenComponentSearchIndex(index)}
                          onChange={(e) =>
                            updateComponentRow(index, {
                              componentProductId: '',
                              componentSearch: e.target.value,
                            })
                          }
                          onBlur={() => {
                            window.setTimeout(() => {
                              setOpenComponentSearchIndex((current) => (current === index ? null : current));
                              setComponentRows((current) =>
                                current.map((entry, rowIndex) => {
                                  if (rowIndex !== index || entry.componentProductId === '') {
                                    return entry;
                                  }
                                  const matchedProduct = productOptions.find((product) => product.id === Number(entry.componentProductId));
                                  if (!matchedProduct) {
                                    return entry;
                                  }
                                  return {
                                    ...entry,
                                    componentSearch: matchedProduct.optionLabel,
                                  };
                                })
                              );
                            }, 120);
                          }}
                          className="w-full"
                        />
                        {openComponentSearchIndex === index && (
                          <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded border border-gray-200 bg-white shadow-lg">
                            {productOptions
                              .filter((product) => product.id !== editing?.id)
                              .filter((product) => {
                                const search = row.componentSearch.trim().toLowerCase();
                                if (!search) {
                                  return true;
                                }
                                const haystack = `${product.sku ?? ''} ${product.name}`.toLowerCase();
                                return haystack.includes(search);
                              })
                              .slice(0, 12)
                              .map((product) => (
                                <button
                                  key={product.id}
                                  type="button"
                                  onMouseDown={(event) => {
                                    event.preventDefault();
                                    updateComponentRow(index, {
                                      componentProductId: product.id,
                                      componentSearch: product.optionLabel,
                                    });
                                    setOpenComponentSearchIndex(null);
                                  }}
                                  className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50"
                                >
                                  {product.optionLabel}
                                </button>
                              ))}
                            {productOptions
                              .filter((product) => product.id !== editing?.id)
                              .filter((product) => {
                                const search = row.componentSearch.trim().toLowerCase();
                                if (!search) {
                                  return true;
                                }
                                const haystack = `${product.sku ?? ''} ${product.name}`.toLowerCase();
                                return haystack.includes(search);
                              })
                              .slice(0, 12).length === 0 && (
                              <div className="px-3 py-2 text-sm text-gray-500">Ingen träff</div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm text-gray-600">Mängd</label>
                        <input
                          type="number"
                          min="0"
                          step={isPieceUom ? '1' : '0.01'}
                          value={row.qty}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            if (isPieceUom && nextValue.includes('.')) {
                              return;
                            }
                            updateComponentRow(index, { qty: nextValue });
                          }}
                          className="w-full"
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm text-gray-600">Enhet</label>
                        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                          {selectedProduct?.uomLabel ?? '-'}
                        </div>
                      </div>

                      <div className="relative col-span-1">
                        <label className="block text-sm text-transparent select-none">X</label>
                        <button
                          type="button"
                          onClick={() =>
                            setConfirmDeleteIndex((current) => (current === index ? null : index))
                          }
                          className="flex h-[42px] w-[42px] items-center justify-center rounded border border-red-200 text-red-600 hover:bg-red-50"
                          aria-label="Ta bort rad"
                          title="Ta bort rad"
                        >
                          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18" />
                            <path d="M8 6V4h8v2" />
                            <path d="M19 6l-1 14H6L5 6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                          </svg>
                        </button>
                        {confirmDeleteIndex === index && (
                          <div className="absolute right-0 top-[calc(100%+6px)] z-10 w-56 rounded border border-red-200 bg-white p-2 shadow-lg">
                            <p className="text-xs text-red-700">Vill du verkligen ta bort raden?</p>
                            <div className="mt-2 flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteIndex(null)}
                                className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                              >
                                Nej
                              </button>
                              <button
                                type="button"
                                onClick={() => removeComponentRow(index)}
                                className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                              >
                                Ja
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600">Anteckningar</label>
              <textarea
                value={form.notes ?? ''}
                onChange={(e) => setForm((current) => ({ ...current, notes: e.target.value }))}
                className="w-full min-h-[90px]"
              />
            </div>

            {editing && editing.priceHistory.length > 0 && (
              <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-3">
                <div>
                  <h4 className="font-medium text-gray-900">Prishistorik</h4>
                  <p className="text-sm text-gray-600">Senaste prisändringarna för aktuell produkt.</p>
                </div>
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Datum</th>
                        <th className="px-2 py-1.5 text-left">Pris exkl. moms</th>
                        <th className="px-2 py-1.5 text-left">Notering</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editing.priceHistory.map((row) => (
                        <tr key={row.id} className="border-t border-gray-200">
                          <td className="px-2 py-1.5">{row.changedAt}</td>
                          <td className="px-2 py-1.5">{formatPrice(row.priceExVat)}</td>
                          <td className="px-2 py-1.5">{row.note ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-2 py-1 bg-success text-white rounded text-sm hover:bg-success-hover disabled:opacity-50"
              >
                {saving ? 'Sparar...' : 'Spara'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
              >
                Avbryt
              </button>
              {editing && (
                <div className="ml-auto">
                  {!showDeactivateConfirm ? (
                    <button
                      type="button"
                      onClick={() => setShowDeactivateConfirm(true)}
                      className="px-2 py-1 rounded text-sm bg-red-600 text-white hover:bg-red-700"
                    >
                      Inaktivera
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2">
                      <span className="text-sm text-red-700">Vill du verkligen inaktivera produkten?</span>
                      <button
                        type="button"
                        onClick={() => void handleDelete(editing)}
                        disabled={deletingProduct}
                        className="rounded bg-red-600 px-2 py-1 text-sm text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {deletingProduct ? 'Inaktiverar...' : 'Ja'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeactivateConfirm(false)}
                        disabled={deletingProduct}
                        className="rounded border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      >
                        Nej
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
